import { HttpError } from '@/lib/httpError'
import { toApiError } from '@/lib/apiError'

interface User {
  id: string
  name: string
  account: string
  type: 'local' | 'ldap' | 'oidc'
  platform_role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  contract_agreement: boolean | null
  totp_enabled: boolean
  last_login_at?: string
  created_at: string
  updated_at: string
}

export interface Project {
  id: string
  /**
   * The Kubernetes namespace. Fixed for the life of the project: every
   * namespace-scoped permission check resolves the project through it.
   */
  name: string
  /** The name people read and can change. Empty means fall back to `name`. */
  display_name?: string
  description: string
  owner_id: string
  status: string
  created_at: string
  updated_at: string
  owner: User
  role?: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'
}

interface ProjectMembership {
  id: string
  project_id: string
  user_id: string
  role: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'
  created_at: string
  updated_at: string
  project: Project
}

interface ProjectMembershipsResponse {
  limit: number
  offset: number
  projects: ProjectMembership[]
  total: number
}

export interface ProjectsResponse {
  limit: number
  offset: number
  projects: Project[]
  total: number
}

export interface CreateProjectData {
  name: string
  description: string
  owner_id: string
}

/** Only the fields that can change. The namespace is not one of them. */
export interface UpdateProjectDetailsData {
  display_name?: string
  description?: string
}

/** The name to show for a project, with the namespace as the fallback. */
export function projectLabel(project: Pick<Project, 'name' | 'display_name'>): string {
  return project.display_name?.trim() || project.name
}

class ProjectService {
  private baseUrl = '/api/v2/projects'

  async getProjects(offset: number = 0, limit: number = 100): Promise<ProjectsResponse> {
    const response = await fetch(`${this.baseUrl}?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch projects', response.status)
    }

    const data: any = await response.json()

    // Check if response contains project memberships (nested structure) or direct projects
    let projects: Project[]

    if (data.projects && data.projects.length > 0 && data.projects[0].project) {
      // New format: extract projects from memberships and add role
      projects = data.projects.map((membership: ProjectMembership) => ({
        ...membership.project,
        role: membership.role
      }))
    } else {
      // Old format: projects are already in the correct structure
      projects = data.projects || []
    }

    return {
      limit: data.limit,
      offset: data.offset,
      projects,
      total: data.total
    }
  }

  async createProject(data: CreateProjectData): Promise<Project> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to create project', response.status)
    }

    return response.json()
  }

  async requestProjectCreate(data: CreateProjectData): Promise<void> {
    const response = await fetch(`${this.baseUrl}/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to submit project creation request', response.status)
    }
  }

  /**
   * Changes a project's display name and description.
   *
   * There is no rename here on purpose. `name` is the Kubernetes namespace and
   * the backend resolves permissions through it, so a rename in the database
   * would orphan the project's Flows and licenses.
   */
  async updateProjectDetails(projectId: string, data: UpdateProjectDetailsData): Promise<Project> {
    const response = await fetch(`${this.baseUrl}/${projectId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw await toApiError(response, 'errors.updateProjectFailed')
    }

    const body = await response.json()
    return body.project
  }

  async getProjectById(projectId: string): Promise<Project> {
    const response = await fetch(`${this.baseUrl}/${projectId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch project', response.status)
    }

    return response.json()
  }

  async deleteProject(projectId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${projectId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to delete project', response.status)
    }
  }

  async requestProjectDelete(projectId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${projectId}/delete/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to submit project deletion request', response.status)
    }
  }
}

export const projectService = new ProjectService()
